"use client";

import { TypeCta } from "@contentful/types";
import {
  faArrowUpRightFromSquare,
  faFileArrowDown,
} from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "@yardzen/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@yardzen/ui/components/dialog";
import { Spinner } from "@yardzen/ui/components/spinner";
import { Asset } from "contentful";
import { useEffect, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";

export function PDFModal({
  contentfulCTA,
  pdfContent,
  modalCta,
}: {
  contentfulCTA: TypeCta<"WITHOUT_UNRESOLVABLE_LINKS">;
  pdfContent: Asset<"WITHOUT_UNRESOLVABLE_LINKS">;
  modalCta: TypeCta<"WITHOUT_UNRESOLVABLE_LINKS">;
}) {
  pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

  const { title, description } = modalCta.fields;

  const [docArray, setDocArray] = useState<number[]>([]);
  const [pageWidth, setPageWidth] = useState(300);

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    const docArray = Array.from({ length: numPages }, (_, index) => index + 1);
    setDocArray(docArray);
  }

  useEffect(() => {
    // Mobile pages fill the dialog: 96vw dialog minus its p-6 (48px).
    const handleResize = () => {
      setPageWidth(Math.floor(window.innerWidth * 0.96) - 48);
    };

    handleResize();

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          className="w-fit px-8 py-3"
          variant={contentfulCTA.fields.variant}
        >
          {contentfulCTA.fields.buttonText}
        </Button>
      </DialogTrigger>

      {/* White rather than the dialog's default tint: the document is the
          content here, and a coloured frame reads as part of the page. */}
      <DialogContent className="flex h-[96svh] w-[96vw] max-w-none flex-col bg-white">
        <DialogHeader className="flex-row flex-wrap items-baseline justify-between gap-x-4 gap-y-1 space-y-0 pr-8 text-left">
          {/* Arsenal, in the body text colour rather than the dialog's green,
              and as large as the header will carry. */}
          <DialogTitle className="font-display text-brand-secondary-dark text-2xl font-normal md:text-3xl">
            {title}
          </DialogTitle>
          <DialogDescription className="text-brand-secondary-dark hidden text-base md:block">
            {description}
          </DialogDescription>
          <div className="ml-auto flex gap-2">
            <Button
              size="sm"
              startIcon={
                <FontAwesomeIcon icon={faFileArrowDown} className="h-4 w-4" />
              }
              onClick={async () => {
                //all of this is to avoid CORS issues for downloading the PDF
                const url = pdfContent.fields.file?.url;
                if (!url) return;
                try {
                  const response = await fetch(url, { mode: "cors" });
                  const blob = await response.blob();
                  const blobUrl = window.URL.createObjectURL(blob);
                  const link = document.createElement("a");
                  link.href = blobUrl;
                  link.download = pdfContent.fields.title || "download.pdf";
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  window.URL.revokeObjectURL(blobUrl);
                } catch (e) {
                  // Optionally handle error
                  window.open(url, "_blank");
                }
              }}
            >
              Download PDF
            </Button>
            <Button
              size="sm"
              variant="secondary"
              startIcon={
                <FontAwesomeIcon
                  icon={faArrowUpRightFromSquare}
                  className="h-4 w-4"
                />
              }
              onClick={() => {
                window.open(pdfContent.fields.file?.url, "_blank");
              }}
            >
              Open In New Tab
            </Button>
          </div>
        </DialogHeader>
        <div
          id="pdf-container-desktop"
          className="hidden min-h-0 w-full flex-1 sm:block"
        >
          <embed
            type="application/pdf"
            src={pdfContent.fields.file?.url}
            className="h-full w-full"
          />
        </div>
        <div
          id="pdf-container"
          className="min-h-0 w-full flex-1 overflow-y-auto sm:hidden"
        >
          <Document
            file={pdfContent.fields.file?.url}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={<Spinner />}
          >
            {docArray.map((pageNumber) => (
              <Page
                key={pageNumber}
                pageNumber={pageNumber}
                renderAnnotationLayer={false}
                renderTextLayer={false}
                width={pageWidth}
              />
            ))}
          </Document>
        </div>
      </DialogContent>
    </Dialog>
  );
}
